import {ReportType} from '../../types/report-type'

export type LojasRelacionamentoType = Extract<
  ReportType,
  'lojas_quase_churn' | 'lojas_quase_fidelizadas'
>

export const LOJAS_RELACIONAMENTO_TITLES: Record<LojasRelacionamentoType, string> = {
  lojas_quase_churn: 'Lojas quase Churn',
  lojas_quase_fidelizadas: 'Lojas Quase Fidelizadas',
}

export const LOJAS_LIST_FILTERS = [
  'industries',
  'distributors',
  'estadoLojista',
  'cidadeLojista',
  'segmentoLojista',
  'tamanhoUL',
  'faixaRisco',
] as const

export type LojasListFilter = typeof LOJAS_LIST_FILTERS[number]
export type LojasRelacionamentoFilters = Record<LojasListFilter, string[]> & {
  cnpj: string
  nomeLojista: string
  valorUltimoPedidoMin: string
  valorUltimoPedidoMax: string
  apenasUnilever90Dias: string
}

export type LojasRelacionamentoOptions = {
  industries: string[]
  distributors: string[]
  estados: string[]
  cidades: string[]
  segmentos: string[]
  tamanhosUL: string[]
  faixasRisco?: string[]
}

// Accept saved filters containing either strings or react-select options.
export function restoreLojasFilters(data: any = {}): LojasRelacionamentoFilters {
  const list = (key: LojasListFilter): string[] =>
    Array.isArray(data?.[key])
      ? data[key].map((item: any) => String(item?.value ?? item)).filter(Boolean)
      : []
  return {
    industries: list('industries'),
    distributors: list('distributors'),
    estadoLojista: list('estadoLojista'),
    cidadeLojista: list('cidadeLojista'),
    segmentoLojista: list('segmentoLojista'),
    tamanhoUL: list('tamanhoUL'),
    faixaRisco: list('faixaRisco'),
    cnpj: String(data?.cnpj ?? ''),
    nomeLojista: String(data?.nomeLojista ?? ''),
    valorUltimoPedidoMin: String(data?.valorUltimoPedidoMin ?? ''),
    valorUltimoPedidoMax: String(data?.valorUltimoPedidoMax ?? ''),
    apenasUnilever90Dias: String(data?.apenasUnilever90Dias ?? ''),
  }
}

export function validateLojasFilters(filters: LojasRelacionamentoFilters): string | null {
  const minimum = filters.valorUltimoPedidoMin.trim().replace(',', '.')
  const maximum = filters.valorUltimoPedidoMax.trim().replace(',', '.')
  for (const value of [minimum, maximum]) {
    if (value && !/^\d{1,16}(?:\.\d{1,2})?$/.test(value)) {
      return 'Informe valores não negativos com até duas casas decimais para o último pedido.'
    }
  }
  // Compare decimal strings without losing precision for DECIMAL(18,2).
  const cents = (value: string) => {
    const [integer, decimals = ''] = value.split('.')
    return integer.padStart(16, '0') + decimals.padEnd(2, '0')
  }
  if (minimum && maximum && cents(minimum) > cents(maximum)) {
    return 'O valor mínimo do último pedido não pode ser maior que o máximo.'
  }
  const cnpj = filters.cnpj.replace(/[./\s-]/g, '')
  if (cnpj && !/^[A-Za-z0-9]{1,14}$/.test(cnpj)) {
    return 'CNPJ Lojista deve conter até 14 letras ou números.'
  }
  return null
}

export function serializeLojasFilters(
  filters: LojasRelacionamentoFilters,
  type: LojasRelacionamentoType
): Record<string, string> {
  const params: Record<string, string> = {
    cnpj: filters.cnpj.replace(/[./\s-]/g, '').toUpperCase(),
    nomeLojista: filters.nomeLojista.trim(),
    valorUltimoPedidoMin: filters.valorUltimoPedidoMin.trim().replace(',', '.'),
    valorUltimoPedidoMax: filters.valorUltimoPedidoMax.trim().replace(',', '.'),
    apenasUnilever90Dias: filters.apenasUnilever90Dias,
  }
  LOJAS_LIST_FILTERS.forEach((key) => {
    if (key !== 'faixaRisco' || type === 'lojas_quase_churn') {
      // JSON preserves names containing commas, quotes or ampersands.
      params[key] = JSON.stringify(filters[key])
    }
  })
  return params
}

export const LOJAS_COLUMN_LABELS: Record<string, string> = {
  CNPJLojista: 'CNPJ',
  MesReferenciaChurn: 'Mês de referência Churn',
  MesReferencia: 'Mês de referência',
  NomeIndustria: 'Indústria',
  NomeDistribuidor: 'Distribuidor',
  NomeLojista: 'Nome Lojista',
  RazaoSocialLojista: 'Razão Social Lojista',
  EstadoLojista: 'Estado Lojista',
  CidadeLojista: 'Cidade Lojista',
  SegmentoLojista: 'Segmento Lojista',
  TamanhoUL: 'Tamanho UL',
  ApenasUnilever90Dias: 'Cliente exclusivo UL',
  FaixaRisco: 'Risco de Churn',
  DataUltimoPedido: 'Data do último pedido',
  ValorUltimoPedido: 'Valor do último pedido',
  MetodoPagamentoUltimoPedido: 'Pagamento do último pedido',
  CanaldeVendas: 'Canal de vendas',
  VendedorAssociado: 'Vendedor associado',
  NomeVendedor: 'Nome Vendedor',
  DataAtualizacaoModelo: 'Atualização do modelo',
  DataAtualizacaoTabela: 'Atualização da tabela',
}

export function formatLojasValue(column: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (column === 'ValorUltimoPedido' && Number.isFinite(Number(value))) {
    return Number(value).toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'})
  }
  if (column.startsWith('Data')) {
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}:\d{2}(?::\d{2})?))?/)
    if (match) return `${match[3]}/${match[2]}/${match[1]}${match[4] ? ` ${match[4]}` : ''}`
  }
  return String(value)
}
